/** Sidebar list routes — one section per guide type (replaces combined /guides). */

export type GuideTypeKey = 'walkthrough' | 'hint' | 'announcement' | 'checklist' | 'survey' | 'choice';

export interface GuideTypeSection {
  type: GuideTypeKey;
  listPath: string;
  sidebarLabel: string;
  title: string;
  description: string;
  newItemLabel: string;
  emptyTitle: string;
  emptyDescription: string;
}

export const GUIDE_TYPE_SECTIONS: GuideTypeSection[] = [
  {
    type: 'walkthrough',
    listPath: '/guide-blocks',
    sidebarLabel: 'Guide blocks',
    title: 'Guide blocks',
    description: 'Multi-step walkthrough tours that guide users through your product.',
    newItemLabel: 'New guide block',
    emptyTitle: 'No guide blocks yet',
    emptyDescription: 'Create a step-by-step tour to onboard users or highlight key workflows.',
  },
  {
    type: 'hint',
    listPath: '/hints',
    sidebarLabel: 'Hints',
    title: 'Hints',
    description: 'Hotspots and tooltips that nudge users toward features without blocking the UI.',
    newItemLabel: 'New hint',
    emptyTitle: 'No hints yet',
    emptyDescription: 'Add a beacon or tooltip on any element to explain what it does.',
  },
  {
    type: 'announcement',
    listPath: '/announcements',
    sidebarLabel: 'Announcements',
    title: 'Announcements',
    description: 'Banners, modals, and slideouts for launches, updates, and promotions.',
    newItemLabel: 'New announcement',
    emptyTitle: 'No announcements yet',
    emptyDescription: 'Share product news with a banner, modal, or slideout.',
  },
  {
    type: 'checklist',
    listPath: '/checklists',
    sidebarLabel: 'Checklists',
    title: 'Checklists',
    description: 'Onboarding checklists that track task completion inside your app.',
    newItemLabel: 'New checklist',
    emptyTitle: 'No checklists yet',
    emptyDescription: 'Help users complete setup with a checklist of in-app tasks.',
  },
  {
    type: 'choice',
    listPath: '/choices',
    sidebarLabel: 'Choices',
    title: 'Choices',
    description: 'User decision points that branch a guide flow.',
    newItemLabel: 'New choice',
    emptyTitle: 'No choices yet',
    emptyDescription: 'Create a choice step so users pick what happens next in a flow.',
  },
  {
    type: 'survey',
    listPath: '/surveys',
    sidebarLabel: 'Surveys',
    title: 'Surveys',
    description: 'In-product NPS, rating, and text surveys to collect feedback.',
    newItemLabel: 'New survey',
    emptyTitle: 'No surveys yet',
    emptyDescription: 'Collect NPS, ratings, or open feedback without leaving your product.',
  },
];

const BY_TYPE = Object.fromEntries(
  GUIDE_TYPE_SECTIONS.map((s) => [s.type, s]),
) as Record<GuideTypeKey, GuideTypeSection>;

export function guideTypeSection(type: string): GuideTypeSection {
  return BY_TYPE[type as GuideTypeKey] ?? BY_TYPE.walkthrough;
}

export function guideListPath(type: string): string {
  return guideTypeSection(type).listPath;
}

/** Detail page URL for a guide, e.g. `/announcements/:id`. */
export function guideDetailPath(type: string, id: string): string {
  return `${guideListPath(type)}/${id}`;
}

const PREVIEW_ENTITY_TO_GUIDE_TYPE: Record<string, GuideTypeKey> = {
  guide: 'walkthrough',
  walkthrough: 'walkthrough',
  guide_block: 'walkthrough',
  hint: 'hint',
  announcement: 'announcement',
  checklist: 'checklist',
  survey: 'survey',
  choice: 'choice',
};

/** Agent / chat preview links when only entity_type + id are known. */
export function guideDetailPathFromPreviewEntity(entityType: string | undefined, id: string): string {
  const key = PREVIEW_ENTITY_TO_GUIDE_TYPE[(entityType ?? '').toLowerCase()] ?? 'walkthrough';
  return guideDetailPath(key, id);
}
